import { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useTourGuide, TourGuideConfig, TourStep } from "./useTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { useTourGuideHover } from "./useTourGuideHover";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { ROUTE_NAMES } from "@root/constants";

interface UsePermitReviewTourGuideProps {
  allDocuments: any[];
}

export const usePermitReviewTourGuide = ({
  allDocuments,
}: UsePermitReviewTourGuideProps) => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const { setHighlightedItemKey, setActiveTourModule, hoverTooltipsEnabled } =
    useTourGuideContext();

  // Tour guide refs
  const pillToggleRef = useRef<HTMLDivElement>(null);
  const uploadFileAreaRef = useRef<HTMLDivElement>(null);
  const dataTableRef = useRef<HTMLDivElement>(null);

  const isPermitReview = pathname === ROUTE_NAMES.PERMIT_REVIEW;

  // Tour guide configuration for permit review
  const permitReviewTourConfig: TourGuideConfig = useMemo(() => {
    if (!isPermitReview) {
      return {
        moduleId: "permitReview",
        steps: [],
        enabled: false,
      };
    }

    const hasDocuments = allDocuments && allDocuments.length > 0;
    const steps: TourStep[] = [
      {
        id: "sidebar-permit",
        targetSelector: '[data-tour-item="permitReview"]',
        title:
          t("tourGuide.permitReview.step1.title") || "Permit Analysis System",
        description:
          t("tourGuide.permitReview.step1.description") ||
          "Welcome to the Permit view! This section helps you extract information from permits, contracts, and estimate documents. Let's explore how it works.",
        position: "right" as const,
        offset: { x: 20, y: 0 },
      },
      {
        id: "permit-pill-toggle",
        targetRef: pillToggleRef,
        title: t("tourGuide.permitReview.step2.title") || "Analysis Modes",
        description:
          t("tourGuide.permitReview.step2.description") ||
          'Switch between "Permit Item Extraction" to extract key information, or "Permit Legal Review" to check for legal compliance issues.',
        position: "bottom" as const,
        offset: { x: 0, y: 12 },
      },
      {
        id: "permit-upload-area",
        targetRef: uploadFileAreaRef,
        title: t("tourGuide.permitReview.step3.title") || "Upload Documents",
        description:
          t("tourGuide.permitReview.step3.description") ||
          'Drag and drop your permit, contract, or estimate documents here. Supported formats: PDF, EXCEL, WORD. You can also click "Choose files" to browse.',
        position: "bottom" as const,
        offset: { x: 0, y: 12 },
      },
      {
        id: "permit-data-table",
        targetRef: dataTableRef,
        title: hasDocuments
          ? t("tourGuide.permitReview.step4.title") || "Manage Uploads"
          : t("tourGuide.permitReview.step4Empty.title") ||
            "Your Uploaded Files",
        description: hasDocuments
          ? t("tourGuide.permitReview.step4.description") ||
            "View all your uploaded documents here. You can edit names, delete files, view output, or start analysis on each document."
          : t("tourGuide.permitReview.step4Empty.description") ||
            "Your uploaded files will appear here once you upload documents. You can then manage, edit, and analyze them.",
        position: "top" as const,
        offset: { x: 0, y: -12 },
      },
    ];

    // Only add step 5 if documents exist
    if (hasDocuments) {
      steps.push({
        id: "permit-document-actions",
        targetSelector: '[data-tour-item="permit-document-actions"]',
        title: t("tourGuide.permitReview.step5.title") || "Document Actions",
        description:
          t("tourGuide.permitReview.step5.description") ||
          'Use "View Output" to see extraction results, or "Start Analysis" to begin processing a document. Edit and delete options are also available.',
        position: "left" as const,
        offset: { x: -20, y: 0 },
      });
    }

    return {
      moduleId: "permitReview",
      enabled: true,
      steps,
    };
  }, [isPermitReview, allDocuments, t]);

  const baseTourGuide = useTourGuide(permitReviewTourConfig);
  
  // Auto-start tour guide and handle completion tracking
  const permitReviewTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourCompletionFlag: "permit_tour_completed",
    isOnTourPath: isPermitReview,
  });

  // Sync tour guide with context
  useEffect(() => {
    if (permitReviewTourGuide.isActive) {
      setActiveTourModule("permitReview");
      if (permitReviewTourGuide.currentStep === 0) {
        setHighlightedItemKey("permitReview");
      } else {
        setHighlightedItemKey(null);
      }
    } else {
      setActiveTourModule(null);
      setHighlightedItemKey(null);
    }
  }, [
    permitReviewTourGuide.isActive,
    permitReviewTourGuide.currentStep,
    setActiveTourModule,
    setHighlightedItemKey,
  ]);

  // Hover tooltips for permit review tour guide
  const permitPillToggleStep = permitReviewTourConfig.steps.find(
    (step) => step.id === "permit-pill-toggle"
  );
  const permitUploadAreaStep = permitReviewTourConfig.steps.find(
    (step) => step.id === "permit-upload-area"
  );
  const permitDataTableStep = permitReviewTourConfig.steps.find(
    (step) => step.id === "permit-data-table"
  );
  const permitDocumentActionsStep = permitReviewTourConfig.steps.find(
    (step) => step.id === "permit-document-actions"
  );

  const permitPillToggleHover = useTourGuideHover(
    "permit-pill-toggle",
    permitPillToggleStep || null
  );
  const permitUploadAreaHover = useTourGuideHover(
    "permit-upload-area",
    permitUploadAreaStep || null
  );
  const permitDataTableHover = useTourGuideHover(
    "permit-data-table",
    permitDataTableStep || null
  );
  const permitDocumentActionsHover = useTourGuideHover(
    "permit-document-actions",
    permitDocumentActionsStep || null
  );

  // Attach hover handlers to permit document actions via selector
  useEffect(() => {
    if (
      !hoverTooltipsEnabled ||
      permitReviewTourGuide.isActive ||
      !permitDocumentActionsStep ||
      !isPermitReview ||
      !allDocuments ||
      allDocuments.length === 0
    )
      return;

    const element = document.querySelector(
      '[data-tour-item="permit-document-actions"]'
    ) as HTMLElement;
    if (element) {
      const handleMouseEnter = () => {
        if (!permitDocumentActionsHover.tooltipData) return;
        permitDocumentActionsHover.hoverProps.onMouseEnter();
      };
      const handleMouseLeave = () => {
        permitDocumentActionsHover.hoverProps.onMouseLeave();
      };

      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [
    hoverTooltipsEnabled,
    permitReviewTourGuide.isActive,
    permitDocumentActionsStep,
    permitDocumentActionsHover.hoverProps,
    permitDocumentActionsHover.tooltipData,
    isPermitReview,
    allDocuments,
  ]);

  return {
    pillToggleRef,
    uploadFileAreaRef,
    dataTableRef,
    permitReviewTourGuide,
    permitReviewTourConfig,
    isPermitReview,
    permitPillToggleHover,
    permitUploadAreaHover,
    permitDataTableHover,
    permitDocumentActionsHover,
  };
};
